import { Context } from './abstractions/context';
import { Resolver } from './types/resolver';
import { ResolverObject } from './types/resolver-object';

export class MapperResolver<TSource, Target> {
  private sourceList!: TSource[];
  private target!: { new (): Target };
  private resolver: Resolver<TSource, Target>[];

  constructor(
    sourceList: TSource[],
    target: { new (): Target },
    resolver: Resolver<TSource, Target>[]
  ) {
    this.sourceList = sourceList;
    this.target = target;
    this.resolver = resolver;
  }

  resolve(context: Context): Target[] {
    return this.sourceList.map((source: TSource, index: number) => {
      context.isFirst = index === 0;
      context.isLast = index === this.sourceList.length - 1;
      const resolved: Target = this.map(
        source,
        this.target,
        this.resolver,
        context
      );
      return resolved;
    });
  }

  private map<TSource, Target>(
    source: TSource,
    destiny: { new (): Target },
    resolver: Resolver<TSource, Target>[],
    context: Context
  ): Target {
    const target: Target = Reflect.construct(destiny, []);

    if (resolver && resolver.length > 0) {
      resolver.forEach((key: Resolver<Target, TSource>) => {
        this.build(source, target, key, context);
      });
    }

    return target;
  }

  private build<TSource, Target>(
    source: TSource,
    destiny: Target,
    resolver: Resolver<TSource, Target>,
    context: Context
  ): void {
    let value;

    if (typeof resolver === 'object') {
      const props: string[] = Object.keys(resolver);

      props.forEach((prop: string) => {
        const propertiesResolver = resolver as ResolverObject<TSource, Target>;

        const propResolver =
          propertiesResolver[prop as keyof ResolverObject<TSource, Target>];

        if (typeof propResolver === 'function') {
          value = propResolver(
            source,
            destiny as Target[keyof Target],
            context
          );
        } else {
          value = propResolver;
        }

        this.setProp(destiny, prop, value as JSONValue);
      });
    } else if (typeof resolver === 'string') {
      let prop = '';
      if (resolver.includes('.')) {
        const props = resolver.split('.');
        value = this.resolveConcatProps(props, source);
        prop = props[props.length - 1];

        this.setProp(destiny, prop, value);
      } else {
        value = source[resolver as keyof TSource];
        this.setProp(destiny, resolver, value as JSONValue);
      }
    }
  }

  private setProp<Target>(
    destiny: Target,
    prop: string,
    value: JSONValue
  ): void {
    Object.defineProperty(destiny as object, prop, {
      value,
      configurable: true,
      writable: true,
      enumerable: true,
    });
  }

  private resolveConcatProps<U>(props: string[], source: U): JSONValue {
    let current = source as JSONValue;

    props.forEach((prop: string) => {
      current = current[prop as keyof JSONValue];
    });

    return current;
  }
}
